import React, { useEffect, useMemo, useState } from 'react';
import { ISites } from 'utils/models/groups';
import ApiService from 'utils/services/ApiService';
import ConfirmationButtons from '../button/ConfirmationButtons';
import { TextField, Tooltip } from '@mui/material';
import { getTestResults, saveDeletionSyncCache, saveSyncCache, saveTestResults } from 'utils/services/idbService';
import useCheckUserIsOnline from 'hooks/useCheckUserOnline';
import { setAllSitesData } from 'utils/redux/reducers/allSitesLoadedReducer';
import { useDispatch } from 'react-redux';
import { updateSiteInGroup, deleteSiteFromGroup } from 'utils/redux/reducers/groupsReducer';

interface SitesProps {
	site: ISites;
	index: number;
	groupId: number;
	moveSite: (from: number, to: number) => void;
	allowUserToEditSite: (siteOwnerIds: number[]) => boolean;
	setShowAddNewSite: React.Dispatch<React.SetStateAction<boolean>>;
	sendPopUpMessage: (type: string, message: string) => void;
}

const Sites: React.FC<SitesProps> = ({
	site,
	groupId,
	index,
	moveSite,
	allowUserToEditSite,
	setShowAddNewSite,
	sendPopUpMessage,
}) => {
	const { isOnline } = useCheckUserIsOnline();
	const [showConfirmationLoading, setShowConfirmationLoading] =
		useState(false);
	const [showEditSite, setShowEditSite] = useState(false);
	const [showDeleteSite, setShowDeleteSite] = useState(false);
	const [siteName, setSiteName] = useState(site.name);
	const [siteToEdit, setSiteToEdit] = useState<ISites>({
		name: '',
		id: 0,
		status: 'true',
		applicationVO: {
			id: 2,
			name: 'Activated Sludge',
			industry: 'Waste Water',
			status: 'Active',
		},
		addressVO: {},
		sortOrder: 999,
		siteIndustryVO: [
			{
				id: 13,
				name: 'Waste Water',
				custom: true,
			},
		],
	});
	const ownerIDs = useMemo(() => {
		return site.coOwnerVOs.map((ownerVO: any) => ownerVO.id);
	}, [site.coOwnerVOs]);
	const dispatch = useDispatch();

	const handleEditSiteName = async () => {
		setShowConfirmationLoading(true);
		
		try {
			if (!isOnline) {
				await saveSyncCache({
					id: site.id,
					name: siteName ?? '',
					groupFK: groupId.toString() ?? '',
					action: 'UPDATE'
				}, "siteSyncCache");

				// Update Redux state
				dispatch(updateSiteInGroup({
					groupId: groupId ?? 0,
					siteId: site.id,
					updates: { name: siteName ?? '', sortOrder: site.sortOrder ?? 0, id: site.id ?? 0 }
				}));
				
				setShowEditSite(false);
				return;
			}

			const result = await ApiService.editASite(siteToEdit, siteToEdit.id);
			
			if (result.status === 200) {
				// Update Redux state
				dispatch(updateSiteInGroup({
					groupId: groupId ?? 0,
					siteId: site.id,
					updates: { name: siteName ?? '', sortOrder: site.sortOrder ?? 0, id: site.id ?? 0 }
				}));

				let cachedData = await getTestResults(
					`allSites`,
					localStorage.getItem('currentUser') ?? 'unknown'
				);

				cachedData.find(
					(cachedSite: { id: number; name: string }) =>
						cachedSite.id === siteToEdit.id
				).name = siteToEdit.name;

				await saveTestResults(
					`allSites`,
					cachedData,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				dispatch(setAllSitesData(cachedData));

				setTimeout(() => {
					setShowEditSite(false);
					setShowConfirmationLoading(false);
				}, 2000);
				sendPopUpMessage('Success', 'Site edited successfully.');
			}
		} catch (error) {
			console.log(error);
			setShowEditSite(false);
			setShowConfirmationLoading(false);
			sendPopUpMessage(
				'Error',
				'Changes not saved. Please refresh your browser and try again.'
			);
		}
	};

	const handleDeleteSite = async (siteId: number) => {
		setShowConfirmationLoading(true);
		try {
			if (!isOnline) {
				// 1. Save to deletion sync cache
				await saveDeletionSyncCache({
					deletedSites: [siteId],
				}, "deletionSyncCache");

				// 2. Save to site sync cache with DELETE action
				// await saveSyncCache({
				// 	id: siteId,
				// 	groupFK: groupId.toString() ?? '',
				// 	name: site.name ?? '',
				// 	action: 'DELETE'
				// }, "siteSyncCache");

				// 3. Update Redux state
				dispatch(deleteSiteFromGroup({
					groupId: groupId ?? 0,
					siteId: siteId
				}));

				// 4. Update local cache
				try {
					// Update allSites cache
					const allSitesCache = await getTestResults(
						`allSites`,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
					const updatedAllSitesCache = allSitesCache.filter(
						(cachedSite: { id: number }) => cachedSite.id !== siteId
					);
					await saveTestResults(
						`allSites`,
						updatedAllSitesCache,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
					dispatch(setAllSitesData(updatedAllSitesCache));

					// Update groupsInfo cache
					const groupsCache = await getTestResults(
						`groupsInfo`,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
					const updatedGroupsCache = groupsCache.map((group: any) => {
						if (group.id === groupId) {
							return {
								...group,
								sites: group.sites.filter((s: any) => s.id !== siteId)
							};
						}
						return group;
					});
					await saveTestResults(
						`groupsInfo`,
						updatedGroupsCache,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
				} catch (cacheError) {
					console.error('Error updating caches:', cacheError);
					sendPopUpMessage('Warning', 'Cache update failed, data may be inconsistent until next sync');
				}

				setShowDeleteSite(false);
				sendPopUpMessage('Success', 'Site marked for deletion. Changes will sync when online.');
				return;
			}

			// Online deletion
			const result = await ApiService.deleteASite(siteId);
			
			if (result.status === 200 || result.status === 204) {
				// Update Redux state
				dispatch(deleteSiteFromGroup({
					groupId: groupId ?? 0,
					siteId: siteId
				}));

				// Update local cache
				try {
					// Update allSites cache
					const allSitesCache = await getTestResults(
						`allSites`,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
					const updatedAllSitesCache = allSitesCache.filter(
						(cachedSite: { id: number }) => cachedSite.id !== siteId
					);
					await saveTestResults(
						`allSites`,
						updatedAllSitesCache,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
					dispatch(setAllSitesData(updatedAllSitesCache));

					// Update groupsInfo cache
					const groupsCache = await getTestResults(
						`groupsInfo`,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
					const updatedGroupsCache = groupsCache.map((group: any) => {
						if (group.id === groupId) {
							return {
								...group,
								sites: group.sites.filter((s: any) => s.id !== siteId)
							};
						}
						return group;
					});
					await saveTestResults(
						`groupsInfo`,
						updatedGroupsCache,
						localStorage.getItem('currentUser') ?? 'unknown'
					);
				} catch (cacheError) {
					console.error('Error updating cache after online deletion:', cacheError);
				}

				sendPopUpMessage('Success', 'Site successfully deleted.');
			} else {
				throw new Error('Server returned unexpected status');
			}
		} catch (error) {
			console.error('Delete site error:', error);
			sendPopUpMessage(
				'Error',
				'Changes not saved. Please refresh your browser and try again.'
			);
		} finally {
			setShowConfirmationLoading(false);
			setShowDeleteSite(false);
		}
	};

	const getSiteInformation = async () => {
		setShowConfirmationLoading(true);
		try {
			const result = await ApiService.getASite(site.id);
			if (result.status !== 200)
				throw Error('Error connecting with the server.');
			setSiteToEdit(result.data);
		} catch (error) {
			console.log(error);
		}
		setShowConfirmationLoading(false);
	};

	const handleEditSiteClick = async () => {
		if (allowUserToEditSite(ownerIDs)) {
			await getSiteInformation();
			setShowAddNewSite(false);
			setSiteName(site.name);
			setShowEditSite(true);
		}
	};

	const handleDeleteSiteClick = async () => {
		if (allowUserToEditSite(ownerIDs)) {
			await getSiteInformation();
			setShowDeleteSite(true);
		}
	};

	const updateSiteValues = (value: string) => {
		setSiteToEdit({
			...siteToEdit,
			name: value,
		});
	};

	const handleSiteName = (e: any) => {
		updateSiteValues(e.target.value);
		setSiteName(e.target.value);
	};

	const updateSiteName = (site: ISites) => {
		setSiteName(site.name);
	};

	useEffect(() => {
		updateSiteName(site);
	}, [site]);

	return (
		<div
			className="!w-[400px] !h-[40px] flex items-center justify-between mb-4 site"
			// ref={(node) => ref(drop(node))}
		>
			{showEditSite && (
				<TextField
					size="small"
					value={siteName}
					style={{
						width: 320,
						marginTop: 0,
						marginRight: 10,
					}}
					sx={{
						'& .MuiInputBase-root': {
							fontFamily: 'Jost, sans-serif',
						},
					}}
					onChange={(e) => handleSiteName(e)}
					// error={errors.groupName !== ''}
					// helperText={errors.groupName}
					disabled={!showEditSite}
					label=""
					key={site.id}
				/>
			)}
			{!showEditSite && (
				<div
					style={{
						backgroundColor: 'rgba(255,255,255,0.8)',
						backdropFilter: 'blur(1px)',
					}}
					className="!font-jost border-[#cbcbcb] mr-[10px] leading-[23px] tracking-[0.2px] !w-[320px] !h-[40px] rounded-[4px] border-[1px] px-[13px] py-[12px]  flex flex-row items-center"
				>
					{siteName}
				</div>
			)}
			<div className="w-[100px]">
				{!showEditSite && !showDeleteSite && (
					<div className="flex flex-row items-center">
						<button
							className={`
                !w-[40px] h-[40px] p-[7px] pl-[11px] border-transparent border-[1px] 
                hover:shadow-sm hover:border-[#ECEDEF] hover:border-[1px] 
                focus:shadow-sm focus:border-[#ECEDEF] focus:border-[1px]
                ${showConfirmationLoading && 'cursor-progress'}
                `}
							type="button"
							onClick={handleEditSiteClick}
						>
							{' '}
							<Tooltip title="Edit site">
								<img
									src="/assets/icons/Edit.svg"
									alt="edit icon"
								/>
							</Tooltip>
						</button>
						<button
							className={`
                !w-[40px] h-[40px] p-[8px] pl-[11px] border-transparent border-[1px] 
                hover:shadow-sm hover:border-[#ECEDEF] hover:border-[1px] 
                focus:shadow-sm focus:border-[#ECEDEF] focus:border-[1px]
                 ${showConfirmationLoading && 'cursor-progress'}
                `}
							disabled={!isOnline}
							type="button"
							onClick={handleDeleteSiteClick}
						>
							<Tooltip title="Delete site">
								<img
									src="/assets/icons/Trash.svg"
									alt="Drawer Icon"
								/>
							</Tooltip>
						</button>
					</div>
				)}
				{showEditSite && (
					<ConfirmationButtons
						handleConfirm={handleEditSiteName}
						handleCancel={() => setShowEditSite(false)}
						disabled={showConfirmationLoading}
					/>
				)}

				{showDeleteSite && (
					<ConfirmationButtons
						handleConfirm={() => handleDeleteSite(site.id)}
						handleCancel={() => setShowDeleteSite(false)}
						disabled={!isOnline || showConfirmationLoading}
					/>
				)}
			</div>
		</div>
	);
};

export default Sites;
